/* eslint-disable max-lines */
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import StickyNote2Icon from '@mui/icons-material/StickyNote2';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { LoadingButton } from '@mui/lab';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Link,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { createLineItemNoteApi, listLineItemNotesApi, noteActionApi } from 'api/patient';
import BatchShippingIcon, { batchShippingTooltip } from 'common/components/BatchShippingIcon';
import BusinessAddressIndicator from 'common/components/BusinessAddressIndicator';
import ShowOriginalToggle from 'common/components/ShowOriginalToggle';
import FillLaterPendingIcon from 'common/components/FillLaterPendingIcon';
import { PotentialDuplicateChip } from 'common/components/PotentialDuplicatePrescription';
import ShipByDateCell from 'common/components/ShipByDateCell';
import { ContextType } from 'common/constants/notes';
import { DISABLED_TAGS_STATUSES } from 'common/constants/orderStatusGroups';
import useInfoDialog from 'common/hooks/useInfoDialog';
import PrescriptionReviewPayloadProvider from 'common/hooks/usePrescriptionReviewPayloadContext';
import { PrintLabelCheckBox, SelectAllCheckBox } from 'common/hooks/usePrintLabelContext';
import useRoles from 'common/hooks/useRoles';
import BatchShipmentDialog from 'common/modules/BatchShipmentDialog';
import AddNoteDialog from 'common/modules/notes/AddNoteDialog';
import NotesDialog from 'common/modules/notes/NotesDialog';
import VerifiedTag from 'common/modules/VerifiedTag';
import {
  SELF_PRESCRIBE_WARNING,
  casingFormat,
  getTrackingInfo,
  isSelfPrescribeMatch,
} from 'common/utils';
import { format } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';
import { ItemType } from 'enums/common.enum';
import lineItemStatusEnum from 'enums/lineItem.enum';
import orderEnum from 'enums/order.enum';
import { CENTRAL_FILL_REVIEW_REASONS } from 'enums/prescription.enum';
import { VerificationStatus } from 'enums/provider.enum';
import { filter, find, first, includes, isEmpty, map, some } from 'lodash';
import CancelLineItemDialog from 'modules/PatientView/components/EligibilityActionItems/CancelLineItemDialog';
import FillOverridesDialog from 'modules/PatientView/components/EligibilityActionItems/FillOverridesDialog';
import ManageTagsDialog from 'modules/PatientView/components/EligibilityActionItems/ManageTagsDialog';
import PatientFillProductButton from 'modules/PatientView/components/EligibilityActionItems/PatientFillProductButton';
import ProductFulfillFailureReasonDialog from 'modules/PatientView/components/EligibilityActionItems/ProductFulfillFailureReasonDialog';
import TransferOutDocumentViewer from 'modules/PatientView/components/EligibilityActionItems/TransferOutDocumentViewer';
import PatientPrescriptionDialog from 'modules/PatientView/components/PrescriptionsTab/PatientPrescriptionDialog';
import { enqueueSnackbar } from 'notistack';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  updateAddressOrderLineItemStatus,
  updateBatchShipmentFilter,
} from 'store/thunks/batchShipmentThunk';
import { handleError } from 'store/thunks/errorHandlerThunks';
import {
  getPatient,
  listPatientPrescriptions,
  updateAddOnQuantity,
  updateLineItemStatus,
} from 'store/thunks/patientThunks';
import { upsertLineItemTags } from 'store/thunks/tagThunks';
import { applyUrgentTag, removeUrgentTag } from 'store/thunks/workQueueThunks';

// POM-6221: why the print checkbox is closed. The box was already disabled on a void order, but
// with no reason given the tooltip fell through to PrintLabelCheckBox's last resort, 'Product in
// review' -- which describes neither the order nor the item, and is what QA saw on hover. A
// VOID order is parked until its Fill Later date, not refused, so it is said in its own words.
// The order is read before the line item because the order is what the operator is looking at.
/**
 * A split leaves the line item PROCESSING with its dispensing quantity reduced; moving the item,
 * or the whole order, parks it at VOID with the quantity untouched. So VOID is the signal that
 * nothing was left behind, and a parked Fill Later order tells the two moves apart.
 */
const fillLaterPendingScope = ({ orderStatus, isOrderFillLater, processingState }) => {
  if (processingState !== 'FILL_LATER') return 'PARTIAL';

  return orderStatus === orderEnum.FILL_LATER && isOrderFillLater ? 'ORDER' : 'ITEM';
};

const labelDisabledReason = ({ orderStatus, itemStatus, processingState }) => {
  if (orderStatus === orderEnum.FILL_LATER) return 'Order is parked for Fill Later';
  if (orderStatus === orderEnum.CANCELED) return 'Order is cancelled';
  if (processingState === 'FILL_LATER') return 'Item is parked for Fill Later';
  if (itemStatus === lineItemStatusEnum.CANCELED || processingState === 'CANCELED')
    return 'Item is cancelled';
  return undefined;
};

const LineItemsTable = ({ order, onFillComplete, enriching, actionsDisabled }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const hasAccess = useRoles();
  const navigate = useNavigate();

  const [tagsItem, setTagsItem] = useState(null); // lineItem being tag-managed
  const [localTagsMap, setLocalTagsMap] = useState({}); // { [lineItemId]: tags[] } overrides
  const [localShipByDateMap, setLocalShipByDateMap] = useState({}); // { [lineItemId]: string }
  const [showOriginalLineItemIds, setShowOriginalLineItemIds] = useState([]);

  const prevLineItemsRef = useRef(null);
  useEffect(() => {
    // When order.lineItems gets a new reference (after a reload), clear stale local overrides
    // so the fresh data from the API are displayed.
    if (prevLineItemsRef.current !== null && prevLineItemsRef.current !== order.lineItems) {
      setLocalTagsMap({});
      setLocalShipByDateMap({});
    }
    prevLineItemsRef.current = order.lineItems;
  }, [order.lineItems]);
  const [menuState, setMenuState] = useState({ anchor: null, li: null });
  const [cancelItem, setCancelItem] = useState(null);
  const [notesMap, setNotesMap] = useState({}); // { [lineItemId]: notes[] }
  const [addNoteItem, setAddNoteItem] = useState(null); // lineItem for add-note dialog
  const [viewNotesItem, setViewNotesItem] = useState(null); // lineItem for view-notes dialog
  const [viewPrescriptionId, setViewPrescriptionId] = useState(null);
  const [loadingRxId, setLoadingRxId] = useState(null);
  const [openBatchShipmentDialog, setOpenBatchShipmentDialog] = useState(false);
  const [currentBatchShipmentId, setCurrentBatchShipmentId] = useState(null);
  const [addToBatchItem, setAddToBatchItem] = useState(null);

  const [alertDismissed, setAlertDismissed] = useState(false);
  useEffect(() => {
    setAlertDismissed(false);
  }, [order.orderId]);

  const selfPrescribeMatch = useMemo(() => {
    const names = new Set();
    const rxNumbers = [];
    order.lineItems?.forEach((li) => {
      if (isSelfPrescribeMatch(order.patientName, li.prescriberName)) {
        names.add(li.prescriberName);
        if (li.rxNumber) rxNumbers.push(li.rxNumber);
      }
    });
    return { names, rxNumbers };
  }, [order.lineItems, order.patientName]);
  const [addToBatchLoading, setAddToBatchLoading] = useState(false);
  const [cannotFillItem, setCannotFillItem] = useState(null);
  const [fillOverridesItem, setFillOverridesItem] = useState(null);
  const [transferDocumentItem, setTransferDocumentItem] = useState(null);
  const [editAddOnItem, setEditAddOnItem] = useState(null);
  const [editAddOnQty, setEditAddOnQty] = useState('');
  const [editAddOnLoading, setEditAddOnLoading] = useState(false);
  const [showAddOnConfirm, setShowAddOnConfirm] = useState(false);

  const handleEditAddOnConfirm = () => {
    setEditAddOnLoading(true);
    dispatch(
      updateAddOnQuantity({
        lineItemId: editAddOnItem.lineItemId,
        orderId: order.orderId,
        mpi: order.mpi,
        quantity: Number(editAddOnQty),
      })
    )
      .then((success) => {
        if (success) {
          enqueueSnackbar('Add On quantity updated successfully', { variant: 'success' });
          return onFillComplete?.();
        }
        enqueueSnackbar('Failed to update Add On quantity', { variant: 'error' });
        return undefined;
      })
      .finally(() => {
        setEditAddOnLoading(false);
        setShowAddOnConfirm(false);
        setEditAddOnItem(null);
        setEditAddOnQty('');
      });
  };

  const handleRxClick = async (prescriptionId) => {
    setLoadingRxId(prescriptionId);
    await Promise.all([
      dispatch(listPatientPrescriptions({ mpi: order.mpi, forceRefresh: false })),
      dispatch(getPatient({ mpi: order.mpi })),
    ]);
    setLoadingRxId(null);
    setViewPrescriptionId(prescriptionId);
  };

  // POM-6598: the duplicate warning names the other prescription only by Rx number, so it is
  // looked up in the patient's prescriptions (same MPI by definition) and opened in the same
  // dialog as the Rx # link.
  const handleRelatedRxClick = async (rxNumber) => {
    setLoadingRxId(rxNumber);
    const [loaded] = await Promise.all([
      dispatch(listPatientPrescriptions({ mpi: order.mpi, forceRefresh: false })),
      dispatch(getPatient({ mpi: order.mpi })),
    ]);
    setLoadingRxId(null);
    const related = find(loaded?.payload?.prescriptions, { rxNumber });
    if (related?.prescriptionUUID) {
      setViewPrescriptionId(related.prescriptionUUID);
    } else {
      enqueueSnackbar(`Rx #${rxNumber} is not in this patient's prescriptions`, {
        variant: 'info',
      });
    }
  };

  const navigateToBatchShipmentQueue = (orderId) => {
    dispatch(updateBatchShipmentFilter({ filter: { orderId } }));
    navigate('/batchShipments');
  };

  const handleOpenBatchShipmentDialog = (batchShipmentId) => {
    setCurrentBatchShipmentId(batchShipmentId);
    setOpenBatchShipmentDialog(true);
  };

  const handleCloseBatchShipmentDialog = () => {
    setCurrentBatchShipmentId(null);
    setOpenBatchShipmentDialog(false);
  };

  const handleAddToBatchConfirm = () => {
    setAddToBatchLoading(true);
    dispatch(
      updateAddressOrderLineItemStatus({
        addressDirectoryId: addToBatchItem.addressDirectoryId,
        lineItemId: addToBatchItem.lineItemId,
        status: 'IN_BATCH_SHIPPING',
        mpi: order.mpi,
      })
    )
      .then((response) => {
        if (response) {
          enqueueSnackbar('Item added to batch shipment', { variant: 'success' });
          return onFillComplete?.();
        }
        enqueueSnackbar('Failed to add item to batch shipment', { variant: 'error' });
        return undefined;
      })
      .finally(() => {
        setAddToBatchLoading(false);
        setAddToBatchItem(null);
      });
  };

  const { InfoDialog, showInfoDialog } = useInfoDialog();

  const isAnyLabelCreated = some(order.lineItems, { isLabelCreated: true });
  const isLineItemsInReprocessingState = some(
    order.lineItems,
    ({ lineItemStatus }) => lineItemStatus === lineItemStatusEnum.RE_PROCESS
  );

  const showLabelColumn =
    hasAccess.generateBulkLabels &&
    isAnyLabelCreated &&
    !order.isProcessedExternal &&
    !isLineItemsInReprocessingState;

  const isOrderShipped = order.orderStatus === orderEnum.SHIPPED;
  const showFillProductColumn = hasAccess.manageProductFulfillmentStatus && !isOrderShipped;
  const showTrackingColumn = isOrderShipped;

  const orderStatusesAllowsUndo = ['OPEN', 'ORDER_VERIFICATION', 'ORDER_RE_VERIFICATION'];

  const handleOpenMenu = (event, li) => setMenuState({ anchor: event.currentTarget, li });
  const handleCloseMenu = () => setMenuState({ anchor: null, li: null });

  // POM-6313: the row menu holds a snapshot of its line item, so a refresh landing while it is
  // open would leave its actions pointed at stale state. Close it rather than let it go stale.
  useEffect(() => {
    if (actionsDisabled) setMenuState({ anchor: null, li: null });
  }, [actionsDisabled]);

  const handleUndo = () => {
    const { li } = menuState;
    handleCloseMenu();
    showInfoDialog({
      title: 'Undo Product Fulfilled',
      message: 'Are you sure, Do you need to undo Product Fulfilled',
      callback: () =>
        dispatch(
          updateLineItemStatus({
            lineItemId: li.lineItemId,
            mpi: order.mpi,
            orderId: order.orderId,
            state: lineItemStatusEnum.PRODUCT_FULFILLMENT,
          })
        ).then((isUndone) => {
          if (isUndone) {
            enqueueSnackbar('Line Item undone to Product Fulfillment', { variant: 'success' });
            return onFillComplete?.();
          }
          enqueueSnackbar('Failed to undo to Product Fulfillment', { variant: 'error' });
          return undefined;
        }),
      cancelable: true,
      confirmButtonText: 'Confirm',
    });
  };

  const handleCancelOpen = () => {
    setCancelItem(menuState.li);
    handleCloseMenu();
  };

  const formatTagText = (tag) => {
    const name = typeof tag === 'string' ? tag : tag?.tagName || '';
    return name
      .split('_')
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
      .join(' ');
  };

  const handleSaveTags = async ({ lineItemId, tags: newTags, urgentShipByDate }) => {
    const currentTags = tagsItem?.tags ?? [];
    const currentTagNames = currentTags.map((t) => t.tagName);
    const newTagNames = (newTags || []).map((t) => t.tagName);
    const urgentAdded = newTagNames.includes('URGENT') && !currentTagNames.includes('URGENT');
    const urgentRemoved = !newTagNames.includes('URGENT') && currentTagNames.includes('URGENT');
    const urgentReAdded =
      Boolean(urgentShipByDate) &&
      newTagNames.includes('URGENT') &&
      currentTagNames.includes('URGENT');

    if (!urgentAdded && !urgentRemoved && !urgentReAdded) {
      return dispatch(upsertLineItemTags({ lineItemId, currentTags, newTags })).then((response) => {
        const updatedTags = first(response?.updatedLineItemTags)?.tags;
        const success = !!response?.updatedLineItemTags;
        if (success) {
          setLocalTagsMap((prev) => ({ ...prev, [lineItemId]: updatedTags ?? [] }));
          setTagsItem(null);
        }
        return success;
      });
    }

    // URGENT tag was added, removed, or re-added with a new ship by date
    let urgentResult;
    if (urgentAdded || urgentReAdded) {
      urgentResult = await dispatch(
        applyUrgentTag({
          lineItemId,
          mpi: order.mpi,
          orderId: order.orderId,
          shipByDate: urgentShipByDate,
        })
      );
    } else {
      urgentResult = await dispatch(
        removeUrgentTag({ lineItemId, mpi: order.mpi, orderId: order.orderId })
      );
    }

    // Also handle any concurrent non-URGENT tag changes
    const nonUrgentCurrentTags = currentTags.filter((t) => t.tagName !== 'URGENT');
    const nonUrgentNewTags = (newTags || []).filter((t) => t.tagName !== 'URGENT');
    const nonUrgentCurrentNames = nonUrgentCurrentTags
      .map((t) => t.tagName)
      .sort()
      .join(',');
    const nonUrgentNewNames = nonUrgentNewTags
      .map((t) => t.tagName)
      .sort()
      .join(',');
    let updatedTags = urgentResult?.tags;
    if (nonUrgentCurrentNames !== nonUrgentNewNames) {
      const tagResponse = await dispatch(
        upsertLineItemTags({
          lineItemId,
          currentTags: nonUrgentCurrentTags,
          newTags: nonUrgentNewTags,
        })
      );
      updatedTags = first(tagResponse?.updatedLineItemTags)?.tags ?? updatedTags;
    }

    setLocalTagsMap((prev) => ({ ...prev, [lineItemId]: updatedTags ?? [] }));
    if (urgentShipByDate) {
      setLocalShipByDateMap((prev) => ({ ...prev, [lineItemId]: urgentShipByDate }));
    }
    setTagsItem(null);
    return true;
  };

  const handleViewNotesOpen = (li) => {
    setNotesMap((prev) => ({ ...prev, [li.lineItemId]: prev[li.lineItemId] ?? [] }));
    setViewNotesItem(li);
    listLineItemNotesApi({
      mpi: order.mpi,
      filter: { noteContext: { noteContextIds: [li.lineItemId], type: ContextType.LINE_ITEM } },
    })
      .then((notes) => setNotesMap((prev) => ({ ...prev, [li.lineItemId]: notes ?? [] })))
      .catch((error) => dispatch(handleError({ error })));
  };

  const handleAddNoteFromMenu = () => {
    setAddNoteItem(menuState.li);
    handleCloseMenu();
  };

  const handleAddNote = (commentContext, content, onComplete) =>
    createLineItemNoteApi({ mpi: order.mpi, content, commentContext })
      .then((newNote) => {
        setNotesMap((prev) => {
          const existing = prev[commentContext.id] ?? [];
          return { ...prev, [commentContext.id]: [newNote, ...existing] };
        });
        onComplete(true);
        return true;
      })
      .catch((error) => {
        dispatch(handleError({ error }));
        onComplete(false);
      });

  const handleUpdateNoteStatus = (noteId, action) => {
    const lineItemId = viewNotesItem?.lineItemId;
    setNotesMap((prev) => {
      const notes = (prev[lineItemId] ?? []).map((n) =>
        n.noteId === noteId ? { ...n, noteState: action } : n
      );
      return { ...prev, [lineItemId]: notes };
    });
    noteActionApi({ noteId, action }).catch((error) => {
      dispatch(handleError({ error }));
      listLineItemNotesApi({
        mpi: order.mpi,
        filter: { noteContext: { noteContextIds: [lineItemId], type: ContextType.LINE_ITEM } },
      }).then((notes) => setNotesMap((prev) => ({ ...prev, [lineItemId]: notes ?? [] })));
    });
  };

  const allActiveLineItemIds = map(
    filter(
      order.lineItems,
      ({ lineItemStatus, isLabelCreated, processingState, taggedForReview }) =>
        order.orderStatus !== orderEnum.CANCELED &&
        lineItemStatus !== lineItemStatusEnum.RE_PROCESS &&
        lineItemStatus !== lineItemStatusEnum.CANCELED &&
        processingState !== 'CANCELED' &&
        !!isLabelCreated &&
        !taggedForReview
    ),
    'lineItemId'
  );

  if (isEmpty(order.lineItems)) {
    return (
      <>
        <InfoDialog />
        {tagsItem && (
          <ManageTagsDialog
            open
            onClose={() => setTagsItem(null)}
            mpi={order.mpi}
            orderId={order.orderId}
            lineItemId={tagsItem.lineItemId}
            existingTags={tagsItem.tags ?? []}
            onSave={handleSaveTags}
            patientDetails={order.patientName ? { patientName: order.patientName } : undefined}
          />
        )}
        <AddNoteDialog
          commentContexType={ContextType.LINE_ITEM}
          commentContextId={addNoteItem?.lineItemId}
          open={!!addNoteItem}
          onClose={() => setAddNoteItem(null)}
          onAddNote={handleAddNote}
        />
        <NotesDialog
          open={!!viewNotesItem}
          onClose={() => setViewNotesItem(null)}
          itemNotes={notesMap[viewNotesItem?.lineItemId] ?? []}
          onUpdateNoteStatus={handleUpdateNoteStatus}
        />
        <Typography variant='body2' color='text.secondary'>
          No items.
        </Typography>
      </>
    );
  }

  return (
    <>
      <InfoDialog />
      {cancelItem && (
        <CancelLineItemDialog
          mpi={order.mpi}
          orderId={order.orderId}
          rxNumber={cancelItem.rxNumber}
          lineItemId={cancelItem.lineItemId}
          hasFillLater={cancelItem.isMarkedForFillLater}
          open
          onClose={(success) => {
            setCancelItem(null);
            if (success === false) onFillComplete?.();
          }}
        />
      )}
      {tagsItem && (
        <ManageTagsDialog
          open
          onClose={() => setTagsItem(null)}
          mpi={order.mpi}
          orderId={order.orderId}
          lineItemId={tagsItem.lineItemId}
          existingTags={tagsItem.tags ?? []}
          onSave={handleSaveTags}
          patientDetails={order.patientName ? { patientName: order.patientName } : undefined}
        />
      )}
      <AddNoteDialog
        commentContexType={ContextType.LINE_ITEM}
        commentContextId={addNoteItem?.lineItemId}
        open={!!addNoteItem}
        onClose={() => setAddNoteItem(null)}
        onAddNote={handleAddNote}
      />
      <NotesDialog
        open={!!viewNotesItem}
        onClose={() => setViewNotesItem(null)}
        itemNotes={notesMap[viewNotesItem?.lineItemId] ?? []}
        onUpdateNoteStatus={handleUpdateNoteStatus}
      />
      {viewPrescriptionId && (
        <PrescriptionReviewPayloadProvider>
          <PatientPrescriptionDialog
            showRefillOption
            mpi={order.mpi}
            open={!!viewPrescriptionId}
            contextId={viewPrescriptionId}
            closeDialog={() => {
              setViewPrescriptionId(null);
              onFillComplete?.();
            }}
            isPrescriptionsTab
          />
        </PrescriptionReviewPayloadProvider>
      )}
      <BatchShipmentDialog
        batchShipmentId={currentBatchShipmentId}
        onClose={handleCloseBatchShipmentDialog}
        open={openBatchShipmentDialog}
      />
      {addToBatchItem && (
        <Dialog open fullWidth aria-describedby='batch-shipping-confirm-dialog'>
          <DialogTitle id='batch-shipping-confirm-dialog'>Confirm Batch Shipping</DialogTitle>
          <DialogContent>
            <Typography variant='subtitle1' sx={{ m: 2 }}>
              Please confirm to add this item to batch shipping
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setAddToBatchItem(null)}
              variant='outlined'
              color='secondary'
              sx={{ mr: 1 }}
              disabled={addToBatchLoading}
            >
              Cancel
            </Button>
            <LoadingButton
              variant='contained'
              color='primary'
              loading={addToBatchLoading}
              onClick={handleAddToBatchConfirm}
            >
              Confirm
            </LoadingButton>
          </DialogActions>
        </Dialog>
      )}
      {cannotFillItem && (
        <ProductFulfillFailureReasonDialog
          open
          onClose={async (success) => {
            if (success) await onFillComplete?.();
            setCannotFillItem(null);
          }}
          lineItemId={cannotFillItem.lineItemId}
          mpi={order.mpi}
          orderId={order.orderId}
        />
      )}
      {fillOverridesItem && (
        <FillOverridesDialog
          open
          onClose={() => setFillOverridesItem(null)}
          overrides={fillOverridesItem.overrides}
          order={order}
          lineItem={fillOverridesItem.lineItem}
        />
      )}
      {transferDocumentItem && (
        <TransferOutDocumentViewer
          lineItemId={transferDocumentItem}
          open={!!transferDocumentItem}
          onClose={() => setTransferDocumentItem(null)}
        />
      )}
      {editAddOnItem && !showAddOnConfirm && (
        <Dialog
          open
          fullWidth
          maxWidth='xs'
          onClose={() => {
            setEditAddOnItem(null);
            setEditAddOnQty('');
          }}
        >
          <DialogTitle>Edit Add On Quantity</DialogTitle>
          <DialogContent>
            <Typography variant='body2' sx={{ mb: 1 }}>
              Item: <strong>{editAddOnItem.itemName}</strong>
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Typography variant='body2' sx={{ mb: 2 }}>
              Current Add On Quantity: <strong>{editAddOnItem.addOnQuantity}</strong>
            </Typography>
            <TextField
              label='New Quantity'
              type='number'
              fullWidth
              value={editAddOnQty}
              onChange={(e) => setEditAddOnQty(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === '.' || e.key === 'e' || e.key === 'E') e.preventDefault();
              }}
              inputProps={{ min: 1 }}
            />
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setEditAddOnItem(null);
                setEditAddOnQty('');
              }}
              variant='outlined'
              color='secondary'
            >
              Cancel
            </Button>
            <Button
              onClick={() => setShowAddOnConfirm(true)}
              variant='contained'
              color='primary'
              disabled={
                !editAddOnQty ||
                Number(editAddOnQty) <= 0 ||
                Number(editAddOnQty) === editAddOnItem.addOnQuantity
              }
            >
              Submit
            </Button>
          </DialogActions>
        </Dialog>
      )}
      {showAddOnConfirm && (
        <Dialog open fullWidth maxWidth='xs'>
          <DialogTitle>Confirm Update</DialogTitle>
          <DialogContent>
            <Typography variant='subtitle1'>
              Are you sure you want to update the Add On quantity to <strong>{editAddOnQty}</strong>
              ?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setShowAddOnConfirm(false)}
              variant='outlined'
              color='secondary'
              disabled={editAddOnLoading}
            >
              No
            </Button>
            <LoadingButton
              onClick={handleEditAddOnConfirm}
              variant='contained'
              color='primary'
              loading={editAddOnLoading}
            >
              Yes
            </LoadingButton>
          </DialogActions>
        </Dialog>
      )}
      <Collapse in={selfPrescribeMatch.names.size > 0 && !alertDismissed} appear>
        <Alert
          severity='warning'
          onClose={() => setAlertDismissed(true)}
          sx={{
            mb: 1,
            mx: 'auto',
            width: 'fit-content',
            backgroundColor: '#fff3cd',
            color: '#664d03',
            border: '1px solid #ffc107',
          }}
        >
          {SELF_PRESCRIBE_WARNING(selfPrescribeMatch.rxNumbers.join(', '))}
        </Alert>
      </Collapse>
      <Table size='small' sx={{ minWidth: 1300 }}>
        <TableHead sx={{ backgroundColor: 'grey.100' }}>
          <TableRow>
            <TableCell sx={{ width: 90, whiteSpace: 'nowrap' }}>Ship By Date</TableCell>
            <TableCell sx={{ width: 72, whiteSpace: 'nowrap', color: 'text.disabled' }}>
              Needs By
            </TableCell>
            <TableCell sx={{ width: 200 }}>Item</TableCell>
            <TableCell sx={{ width: 88 }}>Rx #</TableCell>
            <TableCell sx={{ width: 150 }}>Prescriber</TableCell>
            <TableCell sx={{ width: 130 }}>
              Strength
              <br />
              <Typography variant='caption' color='text.secondary'>
                Form
              </Typography>
            </TableCell>
            <TableCell align='center' sx={{ width: 72 }}>
              Qty
              <br />
              <Typography variant='caption' color='text.secondary'>
                Pres. / Disp.
              </Typography>
            </TableCell>
            <TableCell align='center' sx={{ width: 60 }}>
              Doc #
            </TableCell>
            {showTrackingColumn && <TableCell sx={{ width: 130 }}>Tracking #</TableCell>}
            <TableCell sx={{ width: 140 }}>Tags</TableCell>
            <TableCell align='center' sx={{ width: 110 }}>
              Status
            </TableCell>
            {showFillProductColumn && <TableCell sx={{ width: 100 }} />}
            <TableCell sx={{ width: 120, py: 0, px: 1 }}>
              {showLabelColumn && <SelectAllCheckBox allLineItemIds={allActiveLineItemIds} />}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {[...order.lineItems]
            .sort((a, b) => (a.itemName ?? '').localeCompare(b.itemName ?? ''))
            .map((li) => {
              const effectiveTags = (
                li.lineItemId in localTagsMap ? localTagsMap[li.lineItemId] : (li.tags ?? [])
              )
                .filter((tag) => (tag?.tagName ?? tag) !== 'BACKFILLED_SHIP_BY')
                .sort((left, right) =>
                  formatTagText(left).localeCompare(formatTagText(right), undefined, {
                    sensitivity: 'base',
                  })
                );
              const effectiveShipByDate =
                li.lineItemId in localShipByDateMap
                  ? localShipByDateMap[li.lineItemId]
                  : li.shipByDate;
              const hasOverride = !!(li.overriddenItemName || li.overriddenStrength);
              const isShowingOriginal = showOriginalLineItemIds.includes(li.lineItemId);
              let displayItemName = li.itemName;
              let displayStrength = li.strength;
              let displayNdc = li.ndc;
              if (hasOverride && isShowingOriginal) {
                displayItemName = li.originalItemName;
                displayNdc = li.originalNdc;
              } else if (hasOverride) {
                displayItemName = li.overriddenItemName;
                displayStrength = li.overriddenStrength;
              }
              return (
                <React.Fragment key={li.lineItemId}>
                  <TableRow sx={{ verticalAlign: 'top' }}>
                    {/* Ship By Date */}
                    <TableCell sx={{ whiteSpace: 'nowrap', pt: 1.5 }}>
                      <ShipByDateCell shipByDate={effectiveShipByDate} tags={effectiveTags} />
                    </TableCell>

                    {/* Needs By — legacy field, dimmed */}
                    <TableCell sx={{ whiteSpace: 'nowrap', pt: 1.5, color: 'text.disabled' }}>
                      <Tooltip title='Legacy field — see Ship By Date' placement='top' arrow>
                        <span>
                          {li.needsByDate
                            ? format(utcToZonedTime(new Date(li.needsByDate), 'utc'), 'MM/dd/yy')
                            : '—'}
                        </span>
                      </Tooltip>
                    </TableCell>

                    {/* Item + NDC + Rx # + copy */}
                    <TableCell sx={{ pt: 1.5 }}>
                      <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
                        <Typography variant='body2' sx={{ fontWeight: 500 }}>
                          {displayItemName}
                        </Typography>
                        {hasOverride && (
                          <ShowOriginalToggle
                            isShowingOriginal={isShowingOriginal}
                            onToggle={() =>
                              setShowOriginalLineItemIds((prev) =>
                                prev.includes(li.lineItemId)
                                  ? prev.filter((id) => id !== li.lineItemId)
                                  : [...prev, li.lineItemId]
                              )
                            }
                          />
                        )}
                        {li.lineItemId && (
                          <Tooltip title={`Item ID: ${li.lineItemId}`}>
                            <IconButton
                              size='small'
                              sx={{ ml: 0.5 }}
                              onClick={() => {
                                navigator.clipboard.writeText(li.lineItemId);
                                enqueueSnackbar('Item ID copied', { variant: 'success' });
                              }}
                            >
                              <ContentCopyIcon fontSize='inherit' />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                      <Typography
                        variant='caption'
                        color={displayNdc ? 'text.secondary' : 'error'}
                        display='block'
                      >
                        NDC: {displayNdc || 'N/A'}
                      </Typography>
                      {li.addOnQuantity != null && (
                        <Chip
                          size='small'
                          label={`Add On Qty: ${li.addOnQuantity}`}
                          sx={{
                            height: 18,
                            fontSize: '0.65rem',
                            mt: 0.5,
                            backgroundColor: theme.palette.info.dark,
                            color: theme.palette.primary.contrastText,
                          }}
                        />
                      )}
                    </TableCell>

                    {/* Rx # */}
                    <TableCell sx={{ pt: 1.5 }}>
                      {/* POM-6598: the duplicate chip sits inline after the Rx number. */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          {li.itemType !== ItemType.OTC && li.prescriptionId ? (
                            <Button
                              variant='text'
                              size='small'
                              onClick={() => handleRxClick(li.prescriptionId)}
                              disabled={loadingRxId === li.prescriptionId}
                              sx={{
                                p: 0,
                                minWidth: 0,
                                textTransform: 'none',
                                fontSize: 'inherit',
                                color: 'text.primary',
                                textDecoration: 'underline',
                                '&:hover': { textDecoration: 'underline' },
                              }}
                            >
                              {loadingRxId === li.prescriptionId ? (
                                <Box
                                  sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}
                                >
                                  <CircularProgress size={16} thickness={4} />
                                </Box>
                              ) : (
                                li.rxNumber || '—'
                              )}
                            </Button>
                          ) : (
                            <Typography variant='body2'>{li.rxNumber || '—'}</Typography>
                          )}
                          {li.showOldRxNumber && (
                            <Tooltip
                              title={`Rx Number updated. Previous Rx Number: ${li.oldRxNumber}`}
                              placement='right'
                            >
                              <ErrorOutlineIcon color='error' fontSize='small' />
                            </Tooltip>
                          )}
                        </Box>
                        <PotentialDuplicateChip
                          duplicateOfRxNumber={li.duplicateOfRxNumber}
                          onRelatedRxClick={
                            loadingRxId === li.duplicateOfRxNumber
                              ? undefined
                              : handleRelatedRxClick
                          }
                        />
                      </Box>
                      {li.itemType && (
                        <Chip
                          size='small'
                          label={li.itemType}
                          sx={{
                            height: 18,
                            fontSize: '0.65rem',
                            mt: 0.5,
                            backgroundColor: theme.palette.primary.main,
                            color: theme.palette.primary.contrastText,
                          }}
                        />
                      )}
                    </TableCell>

                    {/* Prescriber */}
                    <TableCell sx={{ pt: 1.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography variant='body2'>
                          {casingFormat(li.prescriberName ?? '') || '-'}
                        </Typography>
                        {(li.processingState === 'PROCESSING' ||
                          li.processingState === 'READY_FOR_SHIPMENT') &&
                          li.prescriberName && (
                            <VerifiedTag
                              status={
                                VerificationStatus[li.verificationStatus] ||
                                VerificationStatus.UNVERIFIED
                              }
                            />
                          )}
                        {selfPrescribeMatch.names.has(li.prescriberName) && (
                          <Tooltip title={SELF_PRESCRIBE_WARNING(li.rxNumber)}>
                            <WarningAmberIcon sx={{ fontSize: 16, color: 'warning.main' }} />
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>

                    {/* Strength / Form */}
                    <TableCell sx={{ pt: 1.5 }}>
                      <>
                        {displayStrength && (
                          <Typography variant='body2'>{displayStrength}</Typography>
                        )}
                        {li.form && (
                          <Typography variant='caption' color='text.secondary' display='block'>
                            {li.form}
                          </Typography>
                        )}
                        {!displayStrength && !li.form && (
                          <Typography variant='body2' color='text.secondary'>
                            —
                          </Typography>
                        )}
                      </>
                    </TableCell>

                    {/* Qty */}
                    <TableCell align='center' sx={{ pt: 1.5, whiteSpace: 'nowrap' }}>
                      {li.prescribedQuantity ?? '—'} / {li.quantity ?? '—'}
                      {li.isMarkedForFillLater && (
                        <FillLaterPendingIcon
                          lineItemId={li.lineItemId}
                          scope={fillLaterPendingScope({
                            orderStatus: order.orderStatus,
                            isOrderFillLater: order.isFillLater,
                            processingState: li.processingState,
                          })}
                        />
                      )}
                    </TableCell>

                    {/* Doc # */}
                    <TableCell align='center' sx={{ pt: 1.5 }}>
                      <Tooltip title={li.documentId ? '' : 'No Docutrack Number'}>
                        <Typography variant='body2'>{li.documentId || 'N/A'}</Typography>
                      </Tooltip>
                    </TableCell>

                    {/* Tracking # */}
                    {showTrackingColumn && (
                      <TableCell sx={{ pt: 1.5 }}>
                        {li.trackingNumber ? (
                          <Link
                            href={
                              getTrackingInfo({ trackingNumber: li.trackingNumber }).trackingUrl
                            }
                            target='_blank'
                            rel='noopener noreferrer'
                            sx={{ fontSize: '0.875rem' }}
                          >
                            {li.trackingNumber}
                          </Link>
                        ) : (
                          '—'
                        )}
                      </TableCell>
                    )}

                    {/* Tags */}
                    <TableCell sx={{ pt: 1, overflow: 'hidden' }}>
                      {(() => {
                        const tagsDisabled =
                          DISABLED_TAGS_STATUSES.includes(li.processingState) ||
                          order.orderStatus === orderEnum.EXPIRED;
                        return (
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                            {effectiveTags.map((tag) => {
                              const label = formatTagText(tag);
                              const truncated =
                                label.length > 30 ? `${label.slice(0, 28)}…` : label;
                              return (
                                <Chip
                                  key={typeof tag === 'string' ? tag : tag?.tagName}
                                  label={truncated}
                                  size='small'
                                  title={label.length > 30 ? label : undefined}
                                  sx={{
                                    height: 20,
                                    fontSize: '0.7rem',
                                    width: 'fit-content',
                                    maxWidth: '100%',
                                  }}
                                />
                              );
                            })}
                            {(() => {
                              let tagsTooltip = 'Edit tags';
                              if (tagsDisabled)
                                tagsTooltip = 'Tags cannot be managed in this state';
                              else if (isEmpty(effectiveTags)) tagsTooltip = 'Add tags';
                              return (
                                <Tooltip title={tagsTooltip} placement='top'>
                                  <span>
                                    <IconButton
                                      size='small'
                                      disabled={tagsDisabled || actionsDisabled}
                                      onClick={() => setTagsItem({ ...li, tags: effectiveTags })}
                                      sx={{
                                        color: isEmpty(effectiveTags)
                                          ? 'text.disabled'
                                          : 'primary.main',
                                        p: 0.25,
                                        alignSelf: 'flex-start',
                                      }}
                                    >
                                      <LocalOfferIcon sx={{ fontSize: '0.9rem' }} />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              );
                            })()}
                          </Box>
                        );
                      })()}
                    </TableCell>

                    {/* Status + indicators */}
                    <TableCell align='center' sx={{ pt: 1.5 }}>
                      <Chip
                        sx={{
                          backgroundColor:
                            {
                              CANCELED: theme.palette.error.light,
                              SHIPPED: theme.palette.success.light,
                              SPLIT: theme.palette.success.light,
                            }[li.processingState] || theme.palette.warning.light,
                          color: theme.palette.primary.contrastText,
                          height: 28,
                          minWidth: 120,
                        }}
                        label={
                          <Typography sx={{ fontSize: '0.75em', fontWeight: 500 }}>
                            {(() => {
                              const specific = [
                                'CANCELED',
                                'SHIPPED',
                                'IN_SHIPMENT',
                                'READY_FOR_SHIPMENT',
                                'SPLIT',
                                'RESHIPMENT',
                                'TRANSFERRED',
                                'FILL_LATER',
                              ].includes(li.processingState);
                              if (specific) return li.processingState.replace(/_/g, ' ');
                              if (order.orderStatus === orderEnum.EXPIRED) return 'EXPIRED';
                              return 'IN PROGRESS';
                            })()}
                          </Typography>
                        }
                      />
                      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'flex-start',
                            gap: 0.25,
                            mt: 0.5,
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Box
                              sx={{
                                width: 8,
                                height: 8,
                                minWidth: 8,
                                minHeight: 8,
                                borderRadius: '50%',
                                flexShrink: 0,
                                backgroundColor: li.taggedForReview
                                  ? (theme.palette.error.main ?? '#ff0707')
                                  : theme.palette.success.main,
                              }}
                            />
                            <Typography
                              variant='caption'
                              sx={{
                                color: li.taggedForReview
                                  ? (theme.palette.error.main ?? '#ff0707')
                                  : 'success.dark',
                                lineHeight: 1,
                                fontWeight: li.taggedForReview ? 600 : 400,
                              }}
                            >
                              Reviewed
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Box
                              sx={{
                                width: 8,
                                height: 8,
                                minWidth: 8,
                                minHeight: 8,
                                borderRadius: '50%',
                                flexShrink: 0,
                                backgroundColor: li.isPrinted
                                  ? theme.palette.success.main
                                  : (theme.palette.error.main ?? '#ff0707'),
                              }}
                            />
                            <Typography
                              variant='caption'
                              sx={{
                                color: li.isPrinted
                                  ? 'success.dark'
                                  : (theme.palette.error.main ?? '#ff0707'),
                                lineHeight: 1,
                              }}
                            >
                              Printed
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Box
                              sx={{
                                width: 8,
                                height: 8,
                                minWidth: 8,
                                minHeight: 8,
                                borderRadius: '50%',
                                flexShrink: 0,
                                backgroundColor:
                                  li.lineItemStatus === lineItemStatusEnum.PRODUCT_FULFILLED
                                    ? theme.palette.success.dark
                                    : (theme.palette.error.main ?? '#ff0707'),
                              }}
                            />
                            <Typography
                              variant='caption'
                              sx={{
                                color:
                                  li.lineItemStatus === lineItemStatusEnum.PRODUCT_FULFILLED
                                    ? 'success.dark'
                                    : (theme.palette.error.main ?? '#ff0707'),
                                lineHeight: 1,
                              }}
                            >
                              Filled
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    </TableCell>

                    {/* Fill Product */}
                    {showFillProductColumn && (
                      <TableCell align='right' sx={{ pt: 1 }}>
                        {li.processingState === 'TRANSFERRED' && li.transferMetadata ? (
                          <Button
                            variant='text'
                            size='small'
                            onClick={() => setTransferDocumentItem(li.lineItemId)}
                            sx={{
                              fontSize: '0.75rem',
                              textDecoration: 'underline',
                              color: theme.palette.primary.main,
                              minWidth: 'auto',
                              p: '4px 8px',
                            }}
                          >
                            View Transfer Document
                          </Button>
                        ) : (
                          li.processingState !== 'SPLIT' && (
                            <PatientFillProductButton
                              item={li}
                              mpi={order.mpi}
                              orderId={order.orderId}
                              isProcessedExternal={order.isProcessedExternal ?? false}
                              orderStatus={order.orderStatus}
                              fulfillingPharmacyId={order.fulfillingPharmacyId}
                              isEnriching={enriching || actionsDisabled}
                              onSuccess={onFillComplete}
                            />
                          )
                        )}
                      </TableCell>
                    )}

                    {/* Label checkbox + Pinned note + Actions */}
                    <TableCell sx={{ py: 0, pt: 0.5, pl: 1, pr: 0.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                        {showLabelColumn ? (
                          <PrintLabelCheckBox
                            lineItemId={li.lineItemId}
                            isLabelCreated={li.isLabelCreated}
                            isLocked={actionsDisabled}
                            isDisabled={
                              li.taggedForReview ||
                              !li.isLabelCreated ||
                              order.orderStatus === orderEnum.CANCELED ||
                              order.orderStatus === orderEnum.FILL_LATER ||
                              li.lineItemStatus === lineItemStatusEnum.CANCELED ||
                              includes(['CANCELED', 'FILL_LATER'], li.processingState)
                            }
                            disabledReason={labelDisabledReason({
                              orderStatus: order.orderStatus,
                              itemStatus: li.lineItemStatus,
                              processingState: li.processingState,
                            })}
                          />
                        ) : (
                          <Box sx={{ width: 34 }} />
                        )}
                        <Tooltip title={li.hasPinnedNote ? 'View pinned note' : 'No pinned note'}>
                          <span>
                            <IconButton
                              size='small'
                              onClick={() => handleViewNotesOpen(li)}
                              sx={{
                                color: li.hasPinnedNote
                                  ? (theme.palette.warning.warningColour ?? '#FFC107')
                                  : 'text.disabled',
                              }}
                            >
                              <StickyNote2Icon fontSize='small' />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <IconButton
                          size='small'
                          onClick={(e) => handleOpenMenu(e, li)}
                          disabled={actionsDisabled}
                        >
                          <MoreVertIcon fontSize='small' />
                        </IconButton>
                        {li.isFillOverride && (
                          <Tooltip
                            title='Fill override applied. Click to view details.'
                            arrow
                            placement='top'
                          >
                            <WarningAmberIcon
                              sx={{
                                color: 'red',
                                fontSize: 20,
                                cursor: 'pointer',
                                flexShrink: 0,
                              }}
                              onClick={() =>
                                setFillOverridesItem({
                                  lineItemId: li.lineItemId,
                                  overrides: li.fillOverrides,
                                  lineItem: li,
                                })
                              }
                            />
                          </Tooltip>
                        )}
                        {li.processingState === 'TRANSFERRED' &&
                          li.transferMetadata &&
                          !showFillProductColumn && (
                            <Button
                              variant='text'
                              size='small'
                              onClick={() => setTransferDocumentItem(li.lineItemId)}
                              sx={{
                                fontSize: '0.7rem',
                                textDecoration: 'underline',
                                color: theme.palette.primary.main,
                                minWidth: 'auto',
                                p: 0,
                              }}
                            >
                              View Transfer Doc
                            </Button>
                          )}
                        {li.inBatchShipmentQueue &&
                          !includes(
                            ['SHIPPED', 'SPLIT', 'CANCELED', 'RESHIPMENT', 'TRANSFERRED'],
                            li.processingState
                          ) &&
                          !order.isProcessedExternal && (
                            <Tooltip
                              title={batchShippingTooltip({
                                isBusinessAddress: li.isBusinessAddress,
                                isShipped: li.processingState === 'IN_SHIPMENT',
                                isClickable: hasAccess.viewBatchShipments,
                              })}
                              arrow
                              placement='top'
                            >
                              <BatchShippingIcon
                                onClick={
                                  hasAccess.viewBatchShipments
                                    ? () =>
                                        li.processingState === 'IN_SHIPMENT' && li.batchShipmentId
                                          ? handleOpenBatchShipmentDialog(li.batchShipmentId)
                                          : navigateToBatchShipmentQueue(order.orderId)
                                    : undefined
                                }
                                color={
                                  li.processingState === 'IN_SHIPMENT'
                                    ? theme.palette.success.main
                                    : (theme.palette.warning.warningColour ?? '#FFC107')
                                }
                                showBadge={li.isBusinessAddress}
                              />
                            </Tooltip>
                          )}
                        {!li.inBatchShipmentQueue &&
                          li.isBusinessAddress &&
                          !includes(
                            ['SHIPPED', 'SPLIT', 'CANCELED', 'RESHIPMENT', 'TRANSFERRED'],
                            li.processingState
                          ) &&
                          !order.isProcessedExternal && <BusinessAddressIndicator />}
                      </Box>
                    </TableCell>
                  </TableRow>
                  {li.processingState === 'RESHIPMENT' && !isEmpty(li.reshipmentReason) && (
                    <TableRow>
                      <TableCell colSpan={13} sx={{ pt: 0, pb: 1, borderTop: 'none' }}>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            flexWrap: 'wrap',
                            gap: 0.5,
                            pl: 1,
                          }}
                        >
                          <Typography variant='caption' sx={{ fontWeight: 600, mr: 0.5 }}>
                            Reshipment Reason(s):
                          </Typography>
                          {li.reshipmentReason.map((reason) => (
                            <Chip
                              key={reason}
                              size='small'
                              sx={{
                                backgroundColor: theme.palette.info.main,
                                color: theme.palette.primary.contrastText,
                              }}
                              label={
                                <Typography sx={{ fontSize: '0.7rem' }}>
                                  {reason.replace(/_/g, ' ')}
                                </Typography>
                              }
                            />
                          ))}
                        </Box>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              );
            })}
        </TableBody>
      </Table>

      {/* Per-row action menu */}
      <Menu
        anchorEl={menuState.anchor}
        open={!!menuState.anchor}
        onClose={handleCloseMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem disabled={!hasAccess.addPatientNote} onClick={handleAddNoteFromMenu}>
          Add Note
        </MenuItem>
        <MenuItem
          disabled={
            menuState.li?.processingState !== 'PROCESSING' ||
            order.isProcessedExternal ||
            menuState.li?.lineItemStatus === lineItemStatusEnum.PRODUCT_FULFILLMENT ||
            !hasAccess.manageProductFulfillmentStatus ||
            !includes(orderStatusesAllowsUndo, order.orderStatus)
          }
          onClick={handleUndo}
        >
          Undo
        </MenuItem>
        <MenuItem
          disabled={
            order.lineItems.length <= 1 ||
            menuState.li?.processingState !== 'PROCESSING' ||
            order.isProcessedExternal ||
            !hasAccess.manageProductFulfillmentStatus ||
            order.orderStatus === orderEnum.ORDER_VERIFIED ||
            menuState.li?.lineItemStatus === lineItemStatusEnum.PRODUCT_FULFILLED ||
            includes(CENTRAL_FILL_REVIEW_REASONS, menuState.li?.reviewReason)
          }
          onClick={() => {
            setCannotFillItem(menuState.li);
            handleCloseMenu();
          }}
        >
          Cannot Fill
        </MenuItem>
        {menuState.li?.addressDirectoryId &&
          !menuState.li?.inBatchShipmentQueue &&
          menuState.li?.processingState === 'PROCESSING' &&
          !order.reshipmentOf &&
          hasAccess.viewBatchShipments && (
            <MenuItem
              onClick={() => {
                setAddToBatchItem(menuState.li);
                handleCloseMenu();
              }}
            >
              Add to batch shipping
            </MenuItem>
          )}
        {menuState.li?.addOnQuantity != null &&
          hasAccess.updateAddOnQuantity &&
          !includes(
            [orderEnum.CANCELED, orderEnum.SHIPPED, orderEnum.EXPIRED],
            order.orderStatus
          ) &&
          !includes([lineItemStatusEnum.CANCELED, 'SPLIT'], menuState.li?.lineItemStatus) &&
          !includes(['SHIPPED', 'IN_SHIPMENT'], menuState.li?.processingState) && (
            <MenuItem
              onClick={() => {
                setEditAddOnItem(menuState.li);
                setEditAddOnQty(String(menuState.li.addOnQuantity));
                handleCloseMenu();
              }}
            >
              Edit Add-On
            </MenuItem>
          )}
        <MenuItem
          disabled={menuState.li?.processingState !== 'PROCESSING' || !hasAccess.cancelOrder}
          onClick={handleCancelOpen}
        >
          Cancel
        </MenuItem>
      </Menu>
    </>
  );
};

LineItemsTable.propTypes = {
  onFillComplete: PropTypes.func,
  enriching: PropTypes.bool,
  actionsDisabled: PropTypes.bool,
  order: PropTypes.shape({
    orderId: PropTypes.string,
    mpi: PropTypes.string,
    patientName: PropTypes.string,
    orderStatus: PropTypes.string,
    isFillLater: PropTypes.bool,
    isProcessedExternal: PropTypes.bool,
    fulfillingPharmacyId: PropTypes.string,
    reshipmentOf: PropTypes.string,
    lineItems: PropTypes.arrayOf(PropTypes.shape({})),
  }).isRequired,
};

LineItemsTable.defaultProps = {
  onFillComplete: undefined,
  enriching: false,
  actionsDisabled: false,
};

export default LineItemsTable;
